import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useIssueSerials, { IssueSerialSelection } from '../hooks/useIssueSerials';
import IssueSerialsFreeTable from './IssueSerialsFreeTable';
import IssueSerialsSelectedPanel from './IssueSerialsSelectedPanel';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  rowData: any;
  partnerId?: string;
  onConfirm: (serials: IssueSerialSelection[]) => void | Promise<void>;
}

const IssueSerialsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  rowData,
  partnerId,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [hasDraftQuantity, setHasDraftQuantity] = React.useState(false);
  const submittingRef = React.useRef(false);
  React.useEffect(() => { setHasDraftQuantity(false); }, [isOpen]);
  const state = useIssueSerials({
    isOpen,
    itemId: rowData?.item?._id,
    partnerId,
    requiredQuantity: rowData?.quantity || 0,
    currentSerial: rowData?.serial,
    currentDeadline: rowData?.deadline,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-6xl max-h-[90vh] overflow-auto bg-white rounded shadow-xl">
        <header className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold">{t('issueSerials.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            title={t('common.cancel')}
          >
            <X size={20} />
          </button>
        </header>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-4">
          <IssueSerialsFreeTable
            serials={state.availableSerials}
            search={state.search}
            onSearchChange={state.setSearch}
            onAdd={state.addSerial}
            loading={state.loading}
          />
          <IssueSerialsSelectedPanel
            ident={rowData?.item?.ident}
            required={state.required}
            selectedQuantity={state.selectedQuantity}
            difference={state.difference}
            serials={state.selectedSerials}
            onQuantityChange={state.updateQuantity}
            onRemove={state.removeSelected}
            onClear={() => { state.clearSelected(); setHasDraftQuantity(false); }}
            onDraftChange={setHasDraftQuantity}
          />
        </div>
        <footer className="flex justify-end gap-2 px-4 py-3 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!state.canConfirm}
            onClick={async () => {
              if (hasDraftQuantity || submittingRef.current) return;
              submittingRef.current = true;
              try {
                await onConfirm(state.selectedSerials);
                onClose();
              } catch (error) {
                console.error('Failed to save issued serials:', error);
              } finally {
                submittingRef.current = false;
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {t('common.confirm')}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default IssueSerialsModal;
